import { Component, OnInit, ViewChild } from '@angular/core';
import { PrimaryButtonComponent } from "../../../../shared/components/primary-button/primary-button.component";
import { LabeledInputComponent } from "../../../../shared/components/labeled-input/labeled-input.component";
import { LogoComponent } from "../../../../shared/components/logo/logo.component";
import { CommonModule } from '@angular/common';
import { SecondaryButtonComponent } from "../../../../shared/components/secondary-button/secondary-button.component";
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { AuthService } from '../../../../core/api/auth.service';
import { firstValueFrom } from 'rxjs';
import { HttpClientModule, HttpErrorResponse } from '@angular/common/http';
import { ToastComponent } from '../../../../shared/components/toast/toast.component';
import { AuthValidatorService } from '../../../../core/services/validators/auth-validator.service';
import { ApiErrorService } from '../../../../core/api/api-error.service';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [PrimaryButtonComponent, LabeledInputComponent, LogoComponent, CommonModule, SecondaryButtonComponent, TranslocoPipe, HttpClientModule, ToastComponent, RouterLink],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.css'
})
export class ResetPasswordComponent implements OnInit {
  // Email Inputs
  emailInput: string = '';
  emailInputError: string | null = null;

  // Password inputs
  passwordInput: string = '';
  passwordInputError: string | null = null;

  // State Variable
  isSend: boolean = false;
  isReset: boolean = false;

  resetToken: string | null = null;

  @ViewChild(ToastComponent) toast!: ToastComponent;

  constructor(private router: Router, private translocoService: TranslocoService, private authService: AuthService, private activatedRoute: ActivatedRoute, private authValidator: AuthValidatorService, private apiError: ApiErrorService) { }

  ngOnInit(): void {
    // Get url query params
    this.activatedRoute.queryParams.subscribe(params => {
      this.emailInput = params['email'] ?? '';
      this.resetToken = params['token'] ?? null;
    });
  }

  signInButtonClick() {
    this.router.navigate(['/signup']);
  }

  async sendInstructionsClick() {
    const emailInputValue = this.emailInput.trim();

    // Validate email
    const emailError = this.authValidator.validateEmail(emailInputValue);
    if (emailError) {
      this.emailInputError = emailError;
      return;
    }

    this.emailInputError = null;
    this.isSend = true;

    // Send instructions to email
    try {
      await firstValueFrom(this.authService.forgotPassword(this.emailInput));
      this.toast.showToast("shared.success", "shared.toast.password_reset_sent", 'success', 3000);
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      this.isSend = false;
      return;
    }
  }

  async resetPasswordClick() {
    const emailInputValue = this.emailInput.trim();
    const passwordInputValue = this.passwordInput.trim();

    // Validate email and password
    const emailError = this.authValidator.validateEmail(emailInputValue);
    const passwordError = this.authValidator.validatePassword(passwordInputValue);

    if (emailError || passwordError) {
      this.emailInputError = emailError;
      this.passwordInputError = passwordError;
      return;
    }

    this.emailInputError = this.passwordInputError = null;
    this.isReset = true;

    // If reset token doesen't exist, return to sign up
    if (!this.resetToken) {
      this.router.navigate(['/auth/signup/']);
      return;
    }

    // Try resetting a password
    try {
      await firstValueFrom(this.authService.resetPassword(this.resetToken!, passwordInputValue));
      this.toast.showToast("shared.success", "shared.toast.password_reset_success", 'success', 3000);
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      this.isReset = false;

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        if (error.status === 400) {
          setTimeout(() => {
            this.router.navigate(['/auth/reset-password/'], { queryParams: {} });
          }, 3000);
        }
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      return;
    }

    setTimeout(() => {
      this.router.navigate(['/auth/signin/'], { queryParams: { email: emailInputValue } });
    }, 3000);
  }
}
